import {
  useMemo,
  useState,
} from 'react'

import type {
  DashboardOverviewRevenue,
  RevenueRangeKey,
} from '../../api/overview/overviewTypes'

import {
  getCurrentRevenueMonths,
  revenueDefaultActiveIndex,
  revenueFrame,
  revenuePlotPointsFromSeries,
  type RevenueRange,
} from '../../data/revenue'

import {
  formatRevenueK,
    niceRevenueMax,
  buildRevenueAxisLabels,
} from '../../utils/chartMath'
import { absoluteBox } from '../../utils/geometry'

import { ChartCanvas } from './ChartCanvas'
import { RevenueGrid } from './RevenueGrid'
import {
  RevenueLineChart,
  revenueTooltipBox,
} from './RevenueLineChart'
import { RevenueRangeSelect } from './RevenueRangeSelect'
import { RevenueTooltip } from './RevenueTooltip'

type RevenueOverviewCardProps = {
  revenue: DashboardOverviewRevenue
}

export const RevenueOverviewCard = ({
  revenue,
}: RevenueOverviewCardProps) => {
  const [range, setRange] =
    useState<RevenueRange>('Monthly')

  /*
    Monthly always starts at the last point,
    which represents the current month.
  */
  const [activeIndex, setActiveIndex] =
    useState(revenueDefaultActiveIndex)

  const sourceSeries =
    revenue.series[
      range as RevenueRangeKey
    ]

  /*
    For Monthly view:
    Ignore old hardcoded month labels coming
    from mock/API and generate the last 6 months
    dynamically from the current date.

    Revenue values remain unchanged.
  */
  const series = useMemo(() => {
    if (range !== 'Monthly') {
      return sourceSeries
    }

    const currentMonths =
      getCurrentRevenueMonths()

    return {
      ...sourceSeries,

      xLabels:
        currentMonths.xLabels,

      periods:
        currentMonths.periods,
    }
  }, [range, sourceSeries])

  const revenueMaxK = useMemo(() => niceRevenueMax(Math.max(...series.valuesK, 0.1)), [series.valuesK])

  const points = useMemo(() =>
    revenuePlotPointsFromSeries(
      series.valuesK,
      series.xLabels,
      series.periods,
      revenueMaxK,
    ),
    [series, revenueMaxK],
  )

  const axisLabels = useMemo(() => buildRevenueAxisLabels(revenueMaxK), [revenueMaxK])

  const hasRevenueData = series.valuesK.some((value) => value > 0)

  const activePoint =
    points[activeIndex] ??
    points[0]

  const tooltip =
    activePoint && hasRevenueData
      ? revenueTooltipBox(activePoint)
      : null

  const handleRangeChange = (
    nextRange: RevenueRange,
  ) => {
    setRange(nextRange)

    /*
      Monthly:
      last point = current month.

      Weekly / Yearly:
      retain existing default behaviour.
    */
    if (nextRange === 'Monthly') {
      setActiveIndex(
        revenueDefaultActiveIndex,
      )

      return
    }

    setActiveIndex(
      revenue.defaultActiveIndex ??
        revenueDefaultActiveIndex,
    )
  }

  return (
    <ChartCanvas
      frame={revenueFrame}
      className="bg-dash-panel"
    >
      <div
        className="absolute flex items-center justify-between"
        style={absoluteBox({
          x: 21,
          y: 25,
          width: 422,
        })}
      >
        <h2 className="font-host text-[16px] font-medium leading-normal text-white">
          Revenue Overview
        </h2>

        <RevenueRangeSelect
          value={range}
          onChange={
            handleRangeChange
          }
        />
      </div>

      <RevenueGrid
        range={range}
        xLabels={series.xLabels}
        activeIndex={
          activeIndex
        }
        axisLabels={axisLabels}
      />

      {hasRevenueData ? (
        <RevenueLineChart
          key={range}
          range={range}
          points={points}
          activeIndex={
            activeIndex
          }
          onActiveIndexChange={
            setActiveIndex
          }
        />
      ) : (
        <div className="absolute inset-x-[60px] top-[145px] text-center text-[14px] leading-[1.4] text-white">
          No revenue data yet
        </div>
      )}

      {activePoint && tooltip ? (
        <RevenueTooltip
          period={
            activePoint.period
          }
          amount={formatRevenueK(
            activePoint.valueK,
          )}
          x={tooltip.x}
          y={tooltip.y}
        />
      ) : null}
    </ChartCanvas>
  )
}